// New issue, as the maintenance tracker draws it: the property, what's
// wrong, the details of the report, who reported it and its photos.
// Create issue opens the issue, where the landlord is told and a contractor
// asked like on any other issue. A caller's prefill (an email, an AI card,
// the EPC register) can carry a category or priority the form doesn't ask.

export function newIssueSlideover() {
  return {
    open: false,
    zIndex: 60,
    saving: false,
    error: null,
    propertyQuery: '',
    propertyResults: [],
    property: null,
    context: null,
    form: null,
    files: [],
    uploading: 0,
    emailThreadId: null,
    candidateId: null,
    proposedActionId: null,

    blankForm() {
      return { title: '', description: '', reported_via: '', category: null, priority: null }
    },

    openNew(prefill = {}) {
      const store = Alpine.store('slideovers')
      this.zIndex = store?.getNextZIndex ? store.getNextZIndex() : 60
      this.form = { ...this.blankForm(), ...Object.fromEntries(Object.entries(prefill).filter(([k]) => ['title', 'description', 'category', 'priority', 'reported_via'].includes(k) && prefill[k] != null)) }
      this.emailThreadId = prefill.email_thread_id || null
      this.candidateId = prefill.email_job_candidate_id || null
      this.proposedActionId = prefill.proposed_action_id || null
      this.files.forEach(f => f.url && !f.emailAttachmentId && URL.revokeObjectURL(f.url))
      // Photos the email came with arrive already on the form — they are
      // copied onto the issue server-side, never downloaded and re-uploaded
      this.files = (prefill.photos || []).map(p => ({
        key: `email-${p.email_attachment_id}`, name: p.filename, image: true, url: p.url,
        emailAttachmentId: p.email_attachment_id, signedId: null, done: true, failed: false
      }))
      this.error = null
      this.property = null
      this.context = null
      this.propertyQuery = ''
      this.propertyResults = []
      this.open = true
      this.toggleAiFab(false)
      if (prefill.property_id) {
        fetch(`/properties/${prefill.property_id}.json`, { headers: { 'Accept': 'application/json' } })
          .then(r => r.ok ? r.json() : null).then(p => { if (p) this.selectProperty(p) })
      }
      this.$nextTick(() => this.$refs.propertyInput?.focus())
    },

    close() { this.open = false; this.toggleAiFab(true) },

    // The AI button sits where the form's buttons are; the other slideovers hide it too
    toggleAiFab(visible) {
      const fab = document.querySelector('.ai-fab-container')
      if (!fab) return
      fab.style.transition = 'opacity 0.2s ease'
      if (visible) {
        fab.style.display = ''
        requestAnimationFrame(() => { fab.style.opacity = '1' })
      } else {
        fab.style.opacity = '0'
        setTimeout(() => { fab.style.display = 'none' }, 200)
      }
    },

    async searchProperties() {
      const q = this.propertyQuery.trim()
      if (q.length < 2) { this.propertyResults = []; return }
      try {
        const response = await fetch(`/properties.json?q=${encodeURIComponent(q)}&limit=10`, { headers: { 'Accept': 'application/json' } })
        if (!response.ok) return
        const data = await response.json()
        this.propertyResults = (Array.isArray(data) ? data : data.properties || []).slice(0, 10)
      } catch (e) {
        console.error('Property search failed:', e)
      }
    },

    async selectProperty(property) {
      this.property = property
      this.propertyResults = []
      this.propertyQuery = property.display_name || property.full_address || property.address || ''
      this.context = null
      try {
        const response = await fetch(`/issues/property_context?property_id=${property.id}`, { headers: { 'Accept': 'application/json' } })
        if (response.ok) this.context = await response.json()
      } catch (e) {
        this.context = null
      }
    },

    clearProperty() {
      this.property = null
      this.context = null
      this.propertyQuery = ''
      this.$nextTick(() => this.$refs.propertyInput?.focus())
    },

    // "Md Hossain and Ruaida Monira", "A, B and C"
    listWords(xs) { return xs.length <= 2 ? xs.join(' and ') : xs.slice(0, -1).join(', ') + ' and ' + xs[xs.length - 1] },

    // The issue panel's pills
    pillClass(tone) {
      return { ok: 'bg-[#dcfce7] text-[#166534]', hold: 'bg-[#f3e8ff] text-[#6b21a8]', accent: 'bg-brand-50 text-brand-500' }[tone] || 'bg-gray-100 text-gray-700'
    },

    // One of the property's issues opens over this panel, which stays as it was
    openIssue(id) {
      window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { id } }))
    },

    // Each file uploads as it is added, shown at once from the browser's copy
    async addFiles(fileList) {
      const { DirectUpload } = await import('@rails/activestorage')
      for (const file of Array.from(fileList || [])) {
        const image = (file.type || '').startsWith('image/')
        const entry = { key: `${file.name}-${Date.now()}-${Math.random()}`, name: file.name, image, url: image ? URL.createObjectURL(file) : null, signedId: null, done: false, failed: false }
        this.files.push(entry)
        this.uploading++
        try {
          const blob = await new Promise((resolve, reject) => new DirectUpload(file, '/rails/active_storage/direct_uploads').create((err, b) => err ? reject(err) : resolve(b)))
          const kept = this.files.find(f => f.key === entry.key)
          if (kept) { kept.signedId = blob.signed_id; kept.done = true }
        } catch (e) {
          const kept = this.files.find(f => f.key === entry.key)
          if (kept) kept.failed = true
        } finally {
          this.uploading--
        }
      }
    },

    removeFile(entry) {
      if (entry.url && !entry.emailAttachmentId) URL.revokeObjectURL(entry.url)
      this.files = this.files.filter(f => f.key !== entry.key)
    },

    get canSave() {
      return !!this.property && !!this.form?.title?.trim() && !!this.form?.reported_via && !this.saving && this.uploading === 0
    },

    async save() {
      if (!this.canSave) return
      this.saving = true
      this.error = null
      try {
        const issue = { title: this.form.title, description: this.form.description, reported_via: this.form.reported_via }
        if (this.form.category) issue.category = this.form.category
        if (this.form.priority) issue.priority = this.form.priority
        const response = await fetch('/issues', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' },
          body: JSON.stringify({
            property_id: this.property.id, signed_blob_ids: this.files.filter(f => f.done && f.signedId).map(f => f.signedId),
            email_attachment_ids: this.files.filter(f => f.emailAttachmentId).map(f => f.emailAttachmentId),
            email_thread_id: this.emailThreadId, email_job_candidate_id: this.candidateId, proposed_action_id: this.proposedActionId, issue
          })
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(data.error || 'Could not create the issue')
        this.close()
        window.dispatchEvent(new CustomEvent('issue-changed'))
        // The inbox's repair row flips to raised
        if (this.candidateId) window.dispatchEvent(new CustomEvent('job-saved', { detail: { id: data.id, emailJobCandidateId: this.candidateId } }))
        window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { id: data.id } }))
      } catch (e) {
        this.error = e.message
      } finally {
        this.saving = false
      }
    }
  }
}
